import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import FluidSubpageHero from '../components/FluidSubpageHero';
import CtaSection from '../components/CtaSection';

export default function Angst() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsData, setCmsData] = useState(null);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/angst_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching angst_side CMS data:', err);
      }
    };
    fetchCmsData();
  }, []);

  const seoTitle = lang === 'da' 
    ? 'Angstlidelser – Udredning & Behandling | Nordisk Psykiatri' 
    : 'Anxiety Disorders – Assessment & Treatment | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Specialiseret udredning og behandling af angstlidelser. Få evidensbaseret psykoterapeutisk hjælp til at genvinde roen og kontrollen i din hverdag.'
    : 'Specialized assessment and treatment of anxiety disorders. Receive evidence-based psychotherapy to regain calm and freedom in your daily life.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  return (
    <div className="bg-sand-light min-h-screen text-text-dark font-sans selection:bg-petrol-dark/10 selection:text-petrol-dark">
      
      {/* ==========================================
          SEKTION 1 – HERO SECTION
          ========================================== */}
      <FluidSubpageHero
        eyebrow={cmsData?.hero_badge ? getLocalizedValue(cmsData.hero_badge) : (lang === 'da' ? "ANGSTLIDELSER" : "ANXIETY DISORDERS")}
        title={cmsData?.hero_overskrift ? getLocalizedValue(cmsData.hero_overskrift) : (lang === 'da' ? 'Behandling af angst i København' : 'Treatment of Anxiety Disorders')}
        description={cmsData?.hero_beskrivelse ? getLocalizedValue(cmsData.hero_beskrivelse) : (lang === 'da'
          ? '<p>Angst er en naturlig reaktion, men når den bliver vedvarende eller begynder at begrænse hverdagen, kan der være tale om en angstlidelse. Den kan vise sig som bekymringer, uro, katastrofetanker, hjertebanken eller undgåelse.</p><p>Hos Nordisk Psykiatri tilbyder vi psykologisk behandling af angst hos unge og voksne i København, med fokus på at forstå og arbejde med det, der udløser og vedligeholder angsten. Du kan booke en indledende samtale uden henvisning. Læs også om <a href="https://www.nordiskpsykiatri.dk/medicinsk-behandling">medicinsk behandling hos psykiater</a>. Har du også svært ved koncentration og struktur, kan du læse om <a href="https://www.nordiskpsykiatri.dk/vidensbank/b2912d08-37c6-41b2-8406-338c6cddf5bb">sammenhængen mellem ADHD og angst</a>.</p>'
          : '<p>Anxiety is a natural biological reaction, but when it becomes persistent or limits your daily life, it may be an anxiety disorder. It can manifest as excessive worry, inner unrest, catastrophic thoughts, palpitations, or avoidance behaviors.</p><p>At Nordisk Psykiatri, we provide evidence-based psychological treatment of anxiety for adolescents and adults in Copenhagen, focusing on understanding and addressing the triggers that maintain anxiety. You can book an initial consultation without a referral.</p>')}
        ctaText={cmsData?.hero_cta_tekst ? getLocalizedValue(cmsData.hero_cta_tekst) : (lang === 'da' ? 'Book en indledende samtale' : 'Book initial consultation')}
        ctaLink={cmsData?.hero_cta_link || cmsData?.cta_knap_link || "/kontakt"}
        secondaryCtaText={lang === 'da' ? 'Se priser' : 'See prices'}
        secondaryCtaLink="/priser"
        reviewStamp={cmsData?.fagligt_gennemgaaet ? getLocalizedValue(cmsData.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Felix Klich, klinisk psykolog | Opdateret september 2026' : 'Clinically reviewed by Felix Klich, clinical psychologist | Updated September 2026')}
        imageUrl={cmsData?.hero_billede?.[0] || 'https://assets.auxodev.dk/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/behandling-af-angst-koebenhavn-nordisk-psykiatri.jpg'}
        imageAlt={lang === 'da' ? 'Kliniklokale hos Nordisk Psykiatri' : 'Consultation room at Nordisk Psykiatri'}
        aspectSquare={true}
        organicShape={false}
        hasFrame={false}
        hasBorder={false}
        hasMask={false}
        hideBackdrop={true}
        imageClass="mix-blend-normal saturate-100 grayscale-0"
      />


      {/* ==========================================
          SEKTION 2 – FORSKELLIGE TYPER AF ANGST
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight mb-3">
              {cmsData?.typer_overskrift ? getLocalizedValue(cmsData.typer_overskrift) : (lang === 'da' ? 'Forskellige typer af angst' : 'Different types of anxiety')}
            </h2>
            {/* Kort accent-streg */}
            <div className="w-9 h-[2px] bg-[#9c8a61] mx-auto mt-4" />
          </div>

          {/* Grid med 6 kort (3 kolonner på desktop) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            
            {/* Kort 1 */}
            <div className="rounded-3xl p-8 bg-sand-medium/80 border border-white/60 shadow-xs flex flex-col items-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-white/60 shadow-xs border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-user text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Social angst' : 'Social anxiety'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Udpræget frygt for at blive vurderet eller ydmyget i sociale situationer. Kan føre til undgåelse af sociale fællesskaber.' : 'Marked fear of being evaluated or humiliated in social situations. Can lead to avoidance of social interactions.'}
              </p>
            </div>

            {/* Kort 2 */}
            <div className="rounded-3xl p-8 bg-sand-medium/80 border border-white/60 shadow-xs flex flex-col items-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-white/60 shadow-xs border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-house text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Panikangst' : 'Panic disorder'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Pludselige og intense anfald af angst med fysiske symptomer som hjertebanken, åndenød og svimmelhed.' : 'Sudden and intense attacks of anxiety accompanied by physical symptoms such as palpitations and shortness of breath.'}
              </p>
            </div>

            {/* Kort 3 */}
            <div className="rounded-3xl p-8 bg-sand-medium/80 border border-white/60 shadow-xs flex flex-col items-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-white/60 shadow-xs border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-cloud text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Generaliseret angst' : 'Generalized anxiety disorder'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Vedvarende bekymring og uro over mange forskellige ting i hverdagen.' : 'Persistent worry and anxiety regarding various everyday situations.'}
              </p>
            </div>

            {/* Kort 4 */}
            <div className="rounded-3xl p-8 bg-sand-medium/80 border border-white/60 shadow-xs flex flex-col items-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-white/60 shadow-xs border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-bullseye text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Specifik fobi' : 'Specific phobia'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Stærk frygt for bestemte ting eller situationer, f.eks. højder, dyr eller at flyve.' : 'Intense fear of specific objects or situations (e.g. heights, animals, flying).'}
              </p>
            </div>

            {/* Kort 5 */}
            <div className="rounded-3xl p-8 bg-sand-medium/80 border border-white/60 shadow-xs flex flex-col items-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-white/60 shadow-xs border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-heart text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Helbredsangst' : 'Health anxiety'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Vedvarende bekymring for alvorlig sygdom på trods af lægens beroligelse.' : 'Persistent worry about serious illness despite medical reassurance.'}
              </p>
            </div>

            {/* Kort 6 */}
            <div className="rounded-3xl p-8 bg-sand-medium/80 border border-white/60 shadow-xs flex flex-col items-center text-center hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-white/60 shadow-xs border border-sand-medium/40 flex items-center justify-center text-petrol-dark mb-5">
                <i className="fa-regular fa-leaf text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Andre angstformer' : 'Other anxiety conditions'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Fx agorafobi, separationsangst, tvangstanker eller angst for at miste kontrollen.' : 'E.g. agoraphobia, separation anxiety, obsessions, or fear of losing control.'}
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 3 – NÅR ANGSTEN FYLDER & CTA BANNER
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          
          {/* Overskrift */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight mb-3">
              {cmsData?.naar_angsten_fylder_overskrift ? getLocalizedValue(cmsData.naar_angsten_fylder_overskrift) : (lang === 'da' ? 'Når angsten fylder' : 'When anxiety takes over')}
            </h2>
            {/* Kort accent-streg */}
            <div className="w-9 h-[2px] bg-[#9c8a61] mx-auto mt-4" />
          </div>

          {/* Indholdsområde i to tekstkolonner */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-14 font-sans text-base sm:text-lg text-text-dark/85 leading-relaxed w-full mb-16">
            <div className="bg-sand-light p-6 sm:p-8 rounded-2xl border border-sand-medium/50 shadow-xs">
              <p>
                {lang === 'da' ? 'Angst kan vise sig forskelligt fra person til person. Nogle oplever mest kropslige symptomer som hjertebanken, svimmelhed eller uro, mens andre især mærker bekymringer, katastrofetanker eller behov for at undgå bestemte situationer.' : 'Anxiety can manifest differently from person to person. Some primarily experience bodily symptoms such as palpitations, dizziness, or restlessness, while others notice catastrophic thoughts and avoidance.'}
              </p>
            </div>
            <div className="bg-sand-light p-6 sm:p-8 rounded-2xl border border-sand-medium/50 shadow-xs">
              <p>
                {lang === 'da' ? 'Selvom symptomerne kan føles overvældende, er angst en tilstand, som kan behandles. Med den rette forståelse, gradvis eksponering og konkrete strategier kan man lære at møde angsten på en ny måde og få mere frihed i hverdagen.' : 'Even when symptoms feel overwhelming, anxiety is a treatable condition. With the right understanding, gradual exposure, and concrete strategies, you can learn to face anxiety and regain freedom in your daily life.'}
              </p>
            </div>
          </div>


          {/* ==========================================
              FREMHÆVET BUDSKAB OM BEHANDLING (CTA BANNER)
              ========================================== */}
          <div className="rounded-3xl p-8 sm:p-10 bg-[#D1E1BE]/40 border border-[#D1E1BE] shadow-xs relative overflow-hidden w-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 relative z-10 pr-0 lg:pr-24">
              
              {/* Rundt ikonfelt til venstre */}
              <div className="w-14 h-14 rounded-full bg-[#D1E1BE] shadow-xs flex items-center justify-center text-petrol-dark shrink-0">
                <i className="fa-regular fa-leaf text-2xl" aria-hidden="true" />
              </div>

              {/* Tekst i banneret */}
              <div className="lg:max-w-2xl">
                <h3 className="font-display text-2xl sm:text-3xl font-medium text-petrol-dark mb-2">
                  {cmsData?.fremhaevet_banner_overskrift ? getLocalizedValue(cmsData.fremhaevet_banner_overskrift) : (lang === 'da' ? 'Behandling kan mindske angsten' : 'Treatment can reduce anxiety')}
                </h3>
                <p className="font-sans text-base sm:text-lg text-text-dark/85 leading-relaxed">
                  {cmsData?.fremhaevet_banner_tekst ? getLocalizedValue(cmsData.fremhaevet_banner_tekst) : (lang === 'da' ? 'Gennem viden, eksponering og nye strategier lærer du at møde angsten på en ny måde, så du gradvist kan få mere frihed og livskvalitet.' : 'Through psychoeducation, exposure, and new strategies, you learn to relate to anxiety in a new way, gradually increasing your freedom and quality of life.')}
                </p>
              </div>
            </div>

            {/* Grafisk element ude i højre side */}
            <div className="hidden lg:flex items-center justify-center absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none w-44 h-44">
              {/* Solid organisk formet overlay bag grafikken */}
              <div className="absolute inset-0 bg-[#D1E1BE]/50 rounded-[58%_42%_63%_37%/43%_57%_43%_57%] z-0 scale-105" />
              <div className="absolute inset-2 bg-[#D1E1BE]/70 rounded-[45%_55%_38%_62%/58%_42%_58%_42%] rotate-12 z-0" />
              
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-4-aug-2026-09-58-09.png"
                alt="Grafisk illustration for behandling af angst"
                className="h-28 sm:h-32 w-auto object-contain opacity-95 relative z-10"
              />
            </div>
          </div>

        </div>
      </section>


      {/* ==========================================
          SEKTION 5 – GRADVIS EKSPONERING ER EN VIGTIG DEL AF BEHANDLINGEN
          ========================================== */}
      <section id="behandling" className="py-16 lg:py-24 bg-sand-light border-b border-sand-medium/40 scroll-mt-12">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Venstre kolonne */}
            <div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight leading-tight mb-3">
                {cmsData?.eksponering_overskrift ? getLocalizedValue(cmsData.eksponering_overskrift) : (
                  <>
                    Gradvis eksponering<br />
                    er en vigtig del af<br />
                    behandlingen
                  </>
                )}
              </h2>
              {/* Kort accent-streg */}
              <div className="w-9 h-[2px] bg-[#9c8a61] mb-6 mt-4" />

              <div className="space-y-4 font-sans text-base sm:text-lg text-text-dark/85 leading-relaxed [&>p]:mb-4 [&>p:last-child]:mb-0">
                {cmsData?.eksponering_tekst ? (
                  <div dangerouslySetInnerHTML={{ __html: getLocalizedValue(cmsData.eksponering_tekst) }} />
                ) : (
                  <>
                    <p>
                      Når vi forstår, hvad der udløser og vedligeholder angsten, kan vi begynde at nærme os det, der føles svært – i et trygt tempo og med støtte.
                    </p>
                    <p>
                      Eksponering handler ikke om at presse sig selv. Det handler om at blive i situationen længe nok til at opleve, at angsten kan stige – og falde igen – uden at du behøver at undgå.
                    </p>
                    <p>
                      Jo flere sådanne erfaringer du får, jo mere plads får du til det, der betyder noget for dig.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Højre kolonne: Illustration */}
            <div className="flex justify-center lg:justify-end items-center">
              <div className="w-full max-w-xl">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-4-aug-2026-09-42-20_1.png"
                  alt="Minimalistisk, terapeutisk line-art kunstnerisk illustration af kvindesilhuet i organiske former"
                  className="w-full h-auto object-contain max-h-[600px] drop-shadow-lg"
                />
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 7 – SÅDAN ARBEJDER VI MED EKSPONERING
          ========================================== */}
      <section className="py-16 sm:py-20 lg:py-24 bg-[#D1E1BE] border-b border-[#c0cbab]">
        <div className="mx-auto max-w-[1450px] px-6 sm:px-8">
          
          <div className="text-center max-w-[850px] mx-auto mb-12 sm:mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[48px] font-normal text-[#171717] tracking-tight leading-[1.1] mb-3.5">
              {cmsData?.proces_overskrift ? getLocalizedValue(cmsData.proces_overskrift) : (lang === 'da' ? 'Sådan arbejder vi med eksponering' : 'How we work with exposure')}
            </h2>
            <p className="font-sans text-base sm:text-base leading-relaxed text-[#252525]">
              {cmsData?.proces_undertekst ? getLocalizedValue(cmsData.proces_undertekst) : (
                lang === 'da' ? (
                  <>
                    Eksponering foregår gradvist og i tæt samarbejde med dig.<br className="hidden sm:inline" />{" "}
                    Målet er ikke at presse angsten væk, men at skabe nye erfaringer med, at du kan være i det svære.
                  </>
                ) : (
                  <>
                    Exposure takes place gradually and in close collaboration with you.<br className="hidden sm:inline" />{" "}
                    The goal is not to force anxiety away, but to build new experiences showing that you can tolerate difficult feelings.
                  </>
                )
              )}
            </p>
          </div>

          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12 lg:gap-8">
            
            {/* Forbindelseslinje på desktop */}
            <div className="hidden lg:block absolute top-[26px] left-[12%] right-[12%] h-[2px] bg-[#1e3a35] z-0 pointer-events-none" />

            {/* Trin 1 */}
            <div className="relative text-center z-10">
              <div className="relative w-[52px] h-[52px] mx-auto mb-8 sm:mb-9 rounded-full bg-[#1e3a35] text-white flex items-center justify-center font-display text-[22px] font-semibold shadow-xs">
                01
                <div className="hidden lg:block absolute top-[52px] left-1/2 -translate-x-1/2 w-[2px] h-[32px] bg-[#1e3a35]" />
              </div>
              <div className="space-y-3">
                <h3 className="font-display text-2xl lg:text-[25px] font-normal text-[#202020] leading-snug">
                  {lang === 'da' ? 'Forberedelse' : 'Preparation'}
                </h3>
                <p className="font-sans text-[15px] leading-[1.65] text-[#222222] max-w-[280px] mx-auto">
                  {lang === 'da' ? 'Vi undersøger sammen, hvilke situationer du undgår, hvad der udløser angsten, og hvad du ønsker at kunne gøre mere frit i din hverdag.' : 'Together, we examine which situations you avoid, what triggers anxiety, and what you wish to do more freely in everyday life.'}
                </p>
              </div>
            </div>

            {/* Trin 2 */}
            <div className="relative text-center z-10">
              <div className="relative w-[52px] h-[52px] mx-auto mb-8 sm:mb-9 rounded-full bg-[#1e3a35] text-white flex items-center justify-center font-display text-[22px] font-semibold shadow-xs">
                02
                <div className="hidden lg:block absolute top-[52px] left-1/2 -translate-x-1/2 w-[2px] h-[32px] bg-[#1e3a35]" />
              </div>
              <div className="space-y-3">
                <h3 className="font-display text-2xl lg:text-[25px] font-normal text-[#202020] leading-snug">
                  {lang === 'da' ? 'Gradvis eksponering' : 'Gradual exposure'}
                </h3>
                <p className="font-sans text-[15px] leading-[1.65] text-[#222222] max-w-[280px] mx-auto">
                  {lang === 'da' ? 'Vi udarbejder et overskueligt hierarki og øver os trin for trin. Tempoet tilpasses dig, så øvelserne er udfordrende, men mulige at gennemføre.' : 'We establish a manageable hierarchy and practice step by step at a pace adapted to you.'}
                </p>
              </div>
            </div>

            {/* Trin 3 */}
            <div className="relative text-center z-10">
              <div className="relative w-[52px] h-[52px] mx-auto mb-8 sm:mb-9 rounded-full bg-[#1e3a35] text-white flex items-center justify-center font-display text-[22px] font-semibold shadow-xs">
                03
                <div className="hidden lg:block absolute top-[52px] left-1/2 -translate-x-1/2 w-[2px] h-[32px] bg-[#1e3a35]" />
              </div>
              <div className="space-y-3">
                <h3 className="font-display text-2xl lg:text-[25px] font-normal text-[#202020] leading-snug">
                  {lang === 'da' ? 'Gentagelse og læring' : 'Repetition and learning'}
                </h3>
                <p className="font-sans text-[15px] leading-[1.65] text-[#222222] max-w-[280px] mx-auto">
                  {lang === 'da' ? 'Gennem gentagelse får hjernen mulighed for at danne nye erfaringer. Over tid kan angsten fylde mindre, og din tillid til egne evner vokse.' : 'Through repetition, the brain creates new experiences, allowing anxiety to diminish over time while your confidence grows.'}
                </p>
              </div>
            </div>

            {/* Trin 4 */}
            <div className="relative text-center z-10">
              <div className="relative w-[52px] h-[52px] mx-auto mb-8 sm:mb-9 rounded-full bg-[#1e3a35] text-white flex items-center justify-center font-display text-[22px] font-semibold shadow-xs">
                04
                <div className="hidden lg:block absolute top-[52px] left-1/2 -translate-x-1/2 w-[2px] h-[32px] bg-[#1e3a35]" />
              </div>
              <div className="space-y-3">
                <h3 className="font-display text-2xl lg:text-[25px] font-normal text-[#202020] leading-snug">
                  {lang === 'da' ? 'Mere frihed i hverdagen' : 'Greater everyday freedom'}
                </h3>
                <p className="font-sans text-[15px] leading-[1.65] text-[#222222] max-w-[280px] mx-auto">
                  {lang === 'da' ? 'Vi arbejder med at omsætte dine værdier til konkrete handlinger. Målet er, at du kan leve mere frit og vælge det, der er vigtigt for dig – også når angsten er til stede.' : 'We work on translating your core values into concrete actions so you can live more freely – even when anxiety is present.'}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 8 – FREMHÆVET CITATBANNER
          ========================================== */}
      <section className="py-16 sm:py-20 lg:py-24 bg-sand-light border-b border-sand-medium/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          <div className="relative w-full bg-[#1e3a35] rounded-3xl p-8 sm:p-12 md:p-14 overflow-hidden text-white shadow-sm">
            
            {/* Grafik i højre side */}
            <div className="absolute right-2 sm:right-6 md:right-10 top-1/2 -translate-y-1/2 pointer-events-none opacity-90 flex items-center justify-end overflow-hidden max-w-[45%] sm:max-w-[40%] lg:max-w-[38%]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-4-aug-2026-10-12-33.png"
                alt="Terapeutisk illustration for frihed og ro i hverdagen"
                className="max-h-[180px] sm:max-h-[230px] md:max-h-[270px] w-auto object-contain drop-shadow-md"
              />
            </div>

            {/* Content Overlay */}
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              
              {/* Text Column */}
              <div className="md:col-span-8 lg:col-span-8 space-y-4">
                <blockquote className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-snug tracking-wide text-white">
                  {cmsData?.citat_tekst ? getLocalizedValue(cmsData.citat_tekst) : (lang === 'da' ? 'Målet er ikke at fjerne alle følelser, men at give dig mere frihed i hverdagen.' : 'The goal is not to eliminate all uncomfortable feelings, but to give you greater freedom in your everyday life.')}
                </blockquote>
                
                {/* Muted Accent Bar */}
                <div className="w-12 h-[2px] bg-white/40 rounded-full mt-5" />
              </div>

            </div>
          </div>
        </div>
      </section>


      {/* ==========================================
          SEKTION 9 – GODE RÅD FRA PSYKOLOGEN
          ========================================== */}
      <section className="py-16 lg:py-24 bg-sand-light">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark tracking-tight mb-3">
              {cmsData?.raad_overskrift ? getLocalizedValue(cmsData.raad_overskrift) : (lang === 'da' ? 'Gode råd fra psykologen' : 'Helpful advice from the psychologist')}
            </h2>
            {/* Kort accent-streg */}
            <div className="w-9 h-[2px] bg-[#9c8a61] mx-auto mt-4" />
          </div>

          {/* Række med 4 råd side om side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Råd 1 */}
            <div className="p-6 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/60 flex items-center justify-center text-petrol-dark mb-5 shadow-xs">
                <i className="fa-regular fa-heart text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Giv angsten navn' : 'Name the anxiety'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Læg mærke til hvad der sker i kroppen og tankerne, uden at forsøge at ændre det.' : 'Notice what happens in your body and thoughts without trying to force it to change.'}
              </p>
            </div>

            {/* Råd 2 */}
            <div className="p-6 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/60 flex items-center justify-center text-petrol-dark mb-5 shadow-xs">
                <i className="fa-regular fa-seedling text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Undgå ikke alt' : 'Do not avoid everything'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Små skridt i retning af det svære hjælper hjernen med at lære, at angsten kan falde igen.' : 'Small steps towards the difficult help the brain learn that anxiety will subside.'}
              </p>
            </div>

            {/* Råd 3 */}
            <div className="p-6 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/60 flex items-center justify-center text-petrol-dark mb-5 shadow-xs">
                <i className="fa-regular fa-comments text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Tal roligt til dig selv' : 'Speak kindly to yourself'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Mød dig selv med venlighed frem for kritik, når angsten fylder.' : 'Approach yourself with warmth and self-compassion rather than criticism when anxiety peaks.'}
              </p>
            </div>

            {/* Råd 4 */}
            <div className="p-6 flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-sand-medium border border-sand-medium/60 flex items-center justify-center text-petrol-dark mb-5 shadow-xs">
                <i className="fa-regular fa-compass text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-display text-2xl font-medium text-petrol-dark mb-2">
                {lang === 'da' ? 'Angst kan ikke kontrolleres' : 'Anxiety cannot be controlled by force'}
              </h3>
              <p className="font-sans text-sm text-text-dark/80 leading-relaxed">
                {lang === 'da' ? 'Jo mere du forsøger at styre angsten, desto mere fylder den ofte. Det hjælper mere at give den plads og lade den passere.' : 'The more you attempt to suppress anxiety, the more room it often occupies. It helps more to give it space and let it pass.'}
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* CTA Section */}
      <CtaSection 
        title={cmsData?.cta_overskrift ? getLocalizedValue(cmsData.cta_overskrift) : undefined}
        description={cmsData?.cta_beskrivelse ? getLocalizedValue(cmsData.cta_beskrivelse) : undefined}
        buttonText={cmsData?.cta_knap_tekst ? getLocalizedValue(cmsData.cta_knap_tekst) : undefined}
        buttonLink={cmsData?.cta_knap_link || cmsData?.hero_cta_link || "/kontakt"}
      />

    </div>
  );
}
